/**
 * Do I Qualify — per-state configuration
 *
 * Source: DMC-10802 "List conditions for all states" (Aurélien), Option B (full lists).
 * Replaces the WordPress-sourced `doIQualify` ACF object.
 *
 * WHY OPTION B: the Option A "core + deltas" table is lossy — reconstructing a
 * state's real list by applying subtractions is how a wrong condition ends up
 * rendered on a medical page. These are the full lists as written in the ticket.
 * Where A and B disagree (notably California), OPTION B WINS — confirmed with
 * Aurélien 2026-07-17. Don't "fix" a list against the Option A table.
 *
 * WHY STATIC: this feature ships on secondary pages (/blog, /medical-conditions)
 * where there is no state context. A REST round-trip mid-flow means a spinner and
 * a failure state inside a conversion funnel. Static makes the lookup synchronous.
 *
 * ── IMPORTANT: LABELS ARE STATUTORY WORDING ─────────────────────────────────
 * `id` exists for matching/sorting/overrides. `label` is what the user SEES, and
 * it deliberately keeps each state's own wording. Montana's statute says "Severe
 * Chronic Pain", Florida's says "Chronic pain" — same `id`, different `label`.
 * Do NOT "tidy up" labels to match each other: broadening a statutory term on a
 * medical page is a claim we can't support.
 * ────────────────────────────────────────────────────────────────────────────
 */

/** Canonical condition IDs. Stable keys — safe to reference from the matrix. */
export type ConditionId = keyof typeof CONDITION_LABELS;

/**
 * Canonical display labels. Used when a state doesn't override `label`.
 *
 * Rule applied when building this: an id must be unique WITHIN a state. Where a
 * state's statute names two things separately (Illinois lists "Epilepsy" AND
 * "Seizures & Seizure Disorders"; "Neuropathy" AND "Peripheral Neuropathy"),
 * they stay separate ids rather than being merged.
 */
export const CONDITION_LABELS = {
  'aids-hiv': 'HIV/AIDS',
  als: 'ALS',
  alzheimers: "Alzheimer's Disease",
  anorexia: 'Anorexia',
  anxiety: 'Anxiety Disorders',
  arthritis: 'Arthritis',
  autism: 'Autism Spectrum Disorder',
  'autoimmune-disease': 'Autoimmune Disease',
  cachexia: 'Cachexia / Wasting Syndrome',
  cancer: 'Cancer',
  'cerebral-palsy': 'Cerebral Palsy',
  'chiari-malformation': 'Chiari Malformation & Syringomyelia',
  'chronic-kidney-disease': 'Chronic Kidney Disease',
  'chronic-pain': 'Chronic Pain',
  'chronic-pancreatitis': 'Chronic Pancreatitis',
  'cns-nerve-damage': 'CNS Nerve Damage',
  concussion: 'Concussion',
  crohns: "Crohn's Disease",
  crps: 'Complex Regional Pain Syndrome (CRPS)',
  'cystic-fibrosis': 'Cystic Fibrosis',
  cystitis: 'Cystitis',
  'decompensated-cirrhosis': 'Decompensated Cirrhosis',
  'degenerative-spinal-pain':
    'Chronic Neuropathic Pain with Degenerative Spinal Disorders',
  depression: 'Depression',
  dysmenorrhea: 'Dysmenorrhea',
  dystonia: 'Dystonia',
  'eating-disorders': 'Eating Disorders',
  'ehlers-danlos': 'Ehlers-Danlos Syndrome',
  endometriosis: 'Endometriosis',
  'epidermolysis-bullosa': 'Epidermolysis Bullosa',
  epilepsy: 'Epilepsy',
  'female-orgasmic-disorder': 'Female Orgasmic Disorder',
  fibromyalgia: 'Fibromyalgia',
  'friedreichs-ataxia': "Friedreich's Ataxia",
  glaucoma: 'Glaucoma',
  'headache-migraine': 'Migraine / Severe Headaches',
  hepatitis: 'Hepatitis',
  'hospice-palliative': 'Palliative / Hospice Care',
  huntingtons: "Huntington's Disease",
  hydrocephalus: 'Hydrocephalus with Intractable Headache',
  ibd: 'Inflammatory Bowel Disease',
  ibs: 'Irritable Bowel Syndrome',
  'inclusion-body-myositis': 'Inclusion Body Myositis',
  'inflammatory-arthritis': 'Inflammatory Autoimmune Arthritis',
  'inflammatory-polyneuropathy': 'Inflammatory Polyneuropathy',
  insomnia: 'Insomnia',
  'interstitial-cystitis': 'Interstitial Cystitis',
  'intractable-headache': 'Intractable Headache Syndromes',
  'intractable-pain': 'Intractable Pain',
  'lewy-body': 'Lewy Body Dementia',
  lupus: 'Lupus',
  mals: 'MALS Syndrome',
  'mitochondrial-disease': 'Mitochondrial Disease',
  'motor-neuron-disease': 'Motor Neuron Disease',
  'movement-disorders': 'Dyskinetic & Spastic Movement Disorders',
  ms: 'Multiple Sclerosis (MS)',
  'muscle-spasms': 'Muscle Spasms',
  'muscular-dystrophy': 'Muscular Dystrophy',
  'myasthenia-gravis': 'Myasthenia Gravis',
  'nail-patella': 'Nail-Patella Syndrome',
  nausea: 'Severe Nausea',
  neurodegenerative: 'Incurable Neurodegenerative Disease',
  'neuropathic-condition': 'Neuropathic Condition',
  'neuropathic-facial-pain': 'Neuropathic Facial Pain',
  neuropathy: 'Neuropathy',
  ocd: 'Obsessive-Compulsive Disorder',
  'opioid-use-disorder': 'Opioid Use Disorder',
  'osteogenesis-imperfecta': 'Osteogenesis Imperfecta',
  'ovarian-cysts': 'Ovarian Cysts',
  parkinsons: "Parkinson's Disease",
  'peripheral-neuropathy': 'Peripheral Neuropathy',
  'post-herpetic-neuralgia': 'Post-Herpetic Neuralgia',
  'post-laminectomy': 'Post-Laminectomy Syndrome',
  'prescription-alternative':
    'Alternative to Addictive Prescription Medication',
  psoriasis: 'Severe Psoriasis & Psoriatic Arthritis',
  'psychiatric-disorders': 'Debilitating Psychiatric Disorders',
  ptsd: 'PTSD',
  'rheumatoid-arthritis': 'Rheumatoid Arthritis',
  seizures: 'Seizures & Seizure Disorders',
  'sickle-cell': 'Sickle Cell Disease',
  'sleep-apnea': 'Obstructive Sleep Apnea',
  'spasmodic-torticollis': 'Spasmodic Torticollis (Cervical Dystonia)',
  spasticity: 'Spasticity',
  'spinal-cord-injury': 'Spinal Cord Injury & Diseases',
  'spinal-cord-spasticity':
    'Spinal Cord Nerve Damage with Intractable Spasticity',
  'spinal-muscular-atrophy': 'Spinal Muscular Atrophy',
  'tarlov-cysts': 'Tarlov Cysts',
  tbi: 'Traumatic Brain Injury (TBI) / Stroke',
  'terminal-illness': 'Terminal Illness',
  'tic-disorder': 'Chronic Vocal / Motor Tic Disorder',
  tourette: 'Tourette Syndrome',
  'ulcerative-colitis': 'Ulcerative Colitis',
  'uterine-fibroids': 'Uterine Fibroids',
  'viral-cns': 'Viral / Prion Disease or CNS Infection',
  vulvodynia: 'Vulvodynia',
} as const;

/**
 * Popularity ranking from real Leafwell signup data (DMC-10802 screenshot).
 * Aurélien: pin the top 6–8, alphabetise the rest.
 *
 * Full data (share of signups):
 *   Chronic Pain 48.7% · Anxiety 41.4% · Other 27.5% · PTSD 22.5% ·
 *   Insomnia 5.7% · Migraine 4.0% · Cancer 3.7% · Depression 3.3% ·
 *   Neuropathy 1.9% · Arthritis/RA 1.6%
 *
 * NOTE: "Other" (27.5%) is not a condition — it's the catch-all. See CATCH_ALL_ID.
 */
export const PINNED_CONDITIONS: ConditionId[] = [
  'chronic-pain',
  'anxiety',
  'ptsd',
  'insomnia',
  'headache-migraine',
  'cancer',
  'depression',
  'neuropathy',
];

/** Pseudo-condition for states whose statute has a physician/symptom catch-all. */
export const CATCH_ALL_ID = 'other' as const;

export type StateConditionEntry = {
  id: ConditionId;
  /** State's own statutory wording. Overrides CONDITION_LABELS for display. */
  label?: string;
  /** Qualifier surfaced in the UI (e.g. "only via a symptom clause"). */
  note?: string;
};

/**
 * How a state's condition list behaves. DMC-10802 defines exactly two groups.
 * - `fixed` — Group 2. Statutory list; selecting from it is meaningful.
 * - `none`  — Group 1 (NY, OK, VA, ME, DC). Qualification is entirely at provider
 *             discretion, so there is nothing to ask. Skip the checklist step.
 *             Some Group 1 states still carry a `conditions` array below — those
 *             are the ticket's ILLUSTRATIVE examples, kept as reference. They are
 *             never rendered. See shouldShowConditionsStep().
 */
export type QualifyListType = 'fixed' | 'none';

export type StateQualifyConfig = {
  slug: string;
  name: string;
  listType: QualifyListType;
  /** Statute includes a physician/symptom catch-all → render the "Other" option. */
  hasCatchAll: boolean;
  /** Copy for the catch-all, where the statute's wording matters. */
  catchAllNote?: string;
  conditions: StateConditionEntry[];
  /** Overrides DEFAULT_AGE_RANGE. Set where minors aren't offered. */
  ageRange?: string[];
  /** New patients must attend in person (affects results-screen copy). */
  inPersonRequired?: boolean;
  /** Anything Task 3 / copy needs to know about this state. */
  notes?: string;
};

/**
 * Age buckets.
 *
 * DMC-10802 (conditions) contains no age data. DMC-10795 (copy) does, implicitly:
 * Templates B and C say "remove the Under-18 row" for MA, DE, CT, MT and VT.
 * Those five states therefore use NO_MINORS_AGE_RANGE — the "Under 18" option is
 * not offered at all, rather than offered and then answered with no copy.
 *
 * TODO(maria): confirm WHY with Aurélien. Minors are legally eligible in all five
 * states, so this is likely a Leafwell service decision rather than a statutory
 * one — and the distinction changes what we'd say to a minor who lands there.
 * Virginia's DMC-10802 entry ("any resident 18 or older") may be a sixth case.
 */
export const DEFAULT_AGE_RANGE = ['Under 18', '18-20', '21-34', '35-64', '65+'];

/** Adults only — see DMC-10795 "remove the Under-18 row". */
export const NO_MINORS_AGE_RANGE = ['18-20', '21-34', '35-64', '65+'];

export const STATE_QUALIFY_CONFIG: Record<string, StateQualifyConfig> = {
  // ── Group 1: physician discretion, no statutory list ──────────────────────
  ny: {
    slug: 'new-york',
    name: 'New York',
    listType: 'none',
    hasCatchAll: true,
    catchAllNote: 'A provider decides if cannabis may help your condition.',
    notes:
      'Group 1. No required condition. The list below is illustrative reference only — NOT rendered.',
    conditions: [
      { id: 'chronic-pain', label: 'Chronic pain' },
      { id: 'anxiety', label: 'Anxiety' },
      { id: 'insomnia', label: 'Insomnia' },
      { id: 'depression', label: 'Depression' },
      { id: 'ptsd' },
      { id: 'hospice-palliative', label: 'End-of-life care' },
    ],
  },
  ok: {
    slug: 'oklahoma',
    name: 'Oklahoma',
    listType: 'none',
    hasCatchAll: true,
    catchAllNote: 'Each doctor certifies at their own judgment.',
    notes: 'No statutory conditions. Skip the checklist entirely.',
    conditions: [],
  },
  va: {
    slug: 'virginia',
    name: 'Virginia',
    listType: 'none',
    hasCatchAll: true,
    catchAllNote:
      'A practitioner may recommend cannabis for any diagnosed condition.',
    notes:
      'No condition list. Statute specifies residents 18+ — possible age-gate, needs confirmation.',
    conditions: [],
  },
  me: {
    slug: 'maine',
    name: 'Maine',
    listType: 'none',
    hasCatchAll: true,
    catchAllNote: 'Your provider determines eligibility.',
    notes:
      'Group 1. No official list. The list below is illustrative reference only — NOT rendered.',
    conditions: [
      { id: 'aids-hiv', label: 'AIDS/HIV' },
      { id: 'alzheimers', label: "Alzheimer's" },
      { id: 'als' },
      { id: 'cachexia', label: 'Cachexia / Wasting Syndrome' },
      { id: 'cancer', label: 'Cancer & chemotherapy side-effects' },
      { id: 'chronic-pain', label: 'Chronic intractable pain' },
      { id: 'crohns', label: "Crohn's disease" },
      { id: 'depression' },
      { id: 'glaucoma' },
      { id: 'hepatitis', label: 'Hepatitis C' },
      { id: 'ibs', label: 'IBS & inflammatory bowel diseases' },
      { id: 'ms', label: 'Multiple sclerosis' },
      { id: 'nail-patella', label: 'Nail-patella syndrome' },
      { id: 'nausea', label: 'Severe nausea' },
      { id: 'ptsd' },
      { id: 'seizures', label: 'Seizures (incl. epilepsy)' },
    ],
  },

  dc: {
    slug: 'washington-dc',
    name: 'Washington DC',
    listType: 'none',
    hasCatchAll: true,
    catchAllNote: 'A provider decides if cannabis may help your condition.',
    notes:
      'Group 1, added in the 2026-07-17 DMC-10802 update. No conditions given in the ticket.',
    // TODO(maria): confirm the slug — guessed. Does /medical-card/washington-dc exist?
    conditions: [],
  },

  // ── Group 2: fixed statutory lists ────────────────────────────────────────
  fl: {
    slug: 'florida',
    name: 'Florida',
    listType: 'fixed',
    hasCatchAll: true,
    catchAllNote:
      'A comparable condition to those listed, as determined by a physician.',
    inPersonRequired: true,
    notes: 'New patients must be seen in person.',
    conditions: [
      { id: 'cancer', label: 'Cancer' },
      { id: 'epilepsy', label: 'Epilepsy' },
      { id: 'glaucoma' },
      { id: 'aids-hiv' },
      { id: 'ptsd' },
      { id: 'als' },
      { id: 'crohns', label: "Crohn's disease" },
      { id: 'parkinsons', label: "Parkinson's disease" },
      { id: 'ms', label: 'Multiple sclerosis (MS)' },
      { id: 'chronic-pain', label: 'Chronic pain' },
      {
        id: 'terminal-illness',
        label: 'Terminal condition',
        note: 'Requires a second physician opinion.',
      },
    ],
  },
  ga: {
    slug: 'georgia',
    name: 'Georgia',
    listType: 'fixed',
    hasCatchAll: false,
    notes:
      'SB 220 ("Putting Georgia\'s Patients First Act"), effective 2026-07-01, ' +
      'expanded the program. Source: leafwell.com/medical-card/georgia (live). ' +
      'No physician catch-all — the list is closed. Several conditions carry ' +
      'age/stage qualifiers, kept in `note`.',
    conditions: [
      { id: 'alzheimers', label: "Alzheimer's Disease" },
      { id: 'als', label: "ALS (Lou Gehrig's Disease)" },
      {
        id: 'autism',
        label: 'Autism Spectrum Disorder',
        note: 'Must be severe if the patient is under 18.',
      },
      {
        id: 'cancer',
        label: 'Cancer',
        note: 'End-stage, or when treatment causes wasting illness, nausea or vomiting.',
      },
      { id: 'crohns', label: "Crohn's Disease" },
      { id: 'epidermolysis-bullosa', label: 'Epidermolysis Bullosa' },
      { id: 'aids-hiv', label: 'HIV/AIDS' },
      { id: 'ibd', label: 'Inflammatory Bowel Disease' },
      { id: 'intractable-pain', label: 'Intractable Pain' },
      { id: 'lupus' },
      { id: 'mitochondrial-disease', label: 'Mitochondrial Disease' },
      { id: 'ms', label: 'Multiple Sclerosis (MS)' },
      { id: 'parkinsons', label: "Parkinson's Disease" },
      { id: 'peripheral-neuropathy', label: 'Peripheral Neuropathy' },
      {
        id: 'ptsd',
        label: 'PTSD',
        note: 'Patient must be at least 18 years old.',
      },
      {
        id: 'seizures',
        label: 'Seizure Disorders',
        note: 'Including seizures caused by epilepsy and head trauma.',
      },
      { id: 'sickle-cell', label: 'Sickle Cell Disease' },
      { id: 'arthritis', label: 'Severe Arthritis' },
      { id: 'insomnia', label: 'Severe Insomnia' },
      { id: 'tourette', label: "Tourette's Syndrome" },
      {
        id: 'hospice-palliative',
        label: 'Hospice Program Admittance',
        note: 'Inpatient or outpatient.',
      },
    ],
  },
  oh: {
    slug: 'ohio',
    name: 'Ohio',
    listType: 'fixed',
    hasCatchAll: false,
    conditions: [
      { id: 'aids-hiv', label: 'AIDS/HIV' },
      { id: 'alzheimers', label: "Alzheimer's" },
      { id: 'als' },
      { id: 'cachexia', label: 'Cachexia / Wasting Syndrome' },
      { id: 'cancer' },
      { id: 'chronic-pain', label: 'Chronic Pain (muscle & other)' },
      { id: 'crohns', label: "Crohn's disease" },
      { id: 'epilepsy' },
      { id: 'fibromyalgia' },
      { id: 'glaucoma' },
      { id: 'hepatitis', label: 'Hepatitis (A, B, C)' },
      { id: 'huntingtons', label: "Huntington's disease" },
      { id: 'ibd', label: 'Inflammatory Bowel Diseases (IBDs)' },
      { id: 'ms', label: 'Multiple sclerosis (MS)' },
      { id: 'muscle-spasms', label: 'Muscle Spasms' },
      { id: 'parkinsons', label: "Parkinson's disease" },
      { id: 'ptsd' },
      { id: 'sickle-cell', label: 'Sickle Cell Diseases' },
      { id: 'spinal-cord-injury', label: 'Spinal Cord Injuries & Diseases' },
      { id: 'tourette', label: 'Tourette Syndrome' },
      { id: 'tbi', label: 'Traumatic Brain Injury (TBI) / Stroke' },
    ],
  },
  tx: {
    slug: 'texas',
    name: 'Texas',
    listType: 'fixed',
    hasCatchAll: false,
    notes:
      'Compassionate Use Program — low-THC only. Results copy should reflect this.',
    conditions: [
      { id: 'als' },
      {
        id: 'neurodegenerative',
        label: 'Incurable neurodegenerative disease',
        note: "e.g. Parkinson's, Alzheimer's, Huntington's.",
      },
      { id: 'autism', label: 'Autism' },
      { id: 'cancer' },
      { id: 'chronic-pain', label: 'Chronic pain' },
      { id: 'crohns', label: "Crohn's disease" },
      { id: 'ibd', label: 'Inflammatory bowel diseases' },
      { id: 'ms', label: 'Multiple sclerosis (MS)' },
      { id: 'terminal-illness', label: 'Terminal illness / hospice care' },
      { id: 'ptsd' },
      { id: 'seizures', label: 'Seizure disorders' },
      { id: 'spasticity', label: 'Spasticity' },
      { id: 'tbi', label: 'Traumatic Brain Injury (TBI)' },
    ],
  },
  il: {
    slug: 'illinois',
    name: 'Illinois',
    listType: 'fixed',
    hasCatchAll: false,
    conditions: [
      { id: 'aids-hiv', label: 'AIDS/HIV' },
      { id: 'alzheimers', label: "Alzheimer's" },
      { id: 'als' },
      { id: 'arthritis', label: 'Arthritis' },
      { id: 'autism', label: "Autism / Asperger's" },
      { id: 'cachexia', label: 'Cachexia / Wasting Syndrome' },
      { id: 'cancer' },
      {
        id: 'chiari-malformation',
        label: 'Chiari Malformation & Syringomyelia',
      },
      {
        id: 'chronic-kidney-disease',
        label: 'Chronic Kidney Disease / Renal Failure',
      },
      { id: 'chronic-pain', label: 'Chronic Pain (muscle & other)' },
      { id: 'crps', label: 'Complex Regional Pain Syndrome (CRPS)' },
      { id: 'crohns', label: "Crohn's disease" },
      { id: 'cystitis', label: 'Cystitis' },
      { id: 'dystonia' },
      {
        id: 'eating-disorders',
        label: 'Eating Disorders (anorexia, bulimia, obesity)',
      },
      { id: 'ehlers-danlos', label: 'Ehlers-Danlos Syndrome' },
      { id: 'endometriosis' },
      { id: 'epilepsy' },
      { id: 'female-orgasmic-disorder' },
      { id: 'fibromyalgia' },
      { id: 'glaucoma' },
      { id: 'headache-migraine', label: 'Headache & Migraine' },
      { id: 'hepatitis', label: 'Hepatitis (A, B, C)' },
      { id: 'ibd', label: 'Inflammatory Bowel Diseases' },
      {
        id: 'inflammatory-polyneuropathy',
        label: 'Inflammatory Polyneuropathy',
      },
      { id: 'lupus' },
      { id: 'ms', label: 'Multiple sclerosis (MS)' },
      {
        id: 'muscular-dystrophy',
        label: 'Muscular Dystrophy & Myotonic Disorders',
      },
      {
        id: 'myasthenia-gravis',
        label: 'Myasthenia Gravis & Myoneural Disorders',
      },
      { id: 'nail-patella', label: 'Nail-Patella Syndrome' },
      { id: 'neuropathy' },
      { id: 'ovarian-cysts', label: 'Ovarian Cysts' },
      { id: 'hospice-palliative', label: 'Palliative / Hospice Care' },
      { id: 'parkinsons', label: "Parkinson's disease" },
      { id: 'peripheral-neuropathy', label: 'Peripheral Neuropathy' },
      { id: 'ptsd' },
      { id: 'rheumatoid-arthritis', label: 'Rheumatoid Arthritis' },
      { id: 'seizures', label: 'Seizures & Seizure Disorders' },
      { id: 'spinal-cord-injury', label: 'Spinal Cord Injury & Diseases' },
      { id: 'tarlov-cysts', label: 'Tarlov Cysts' },
      { id: 'tourette', label: 'Tourette Syndrome' },
      { id: 'tbi', label: 'Traumatic Brain Injury / Stroke' },
      { id: 'uterine-fibroids', label: 'Uterine Fibroids' },
    ],
  },
  ma: {
    slug: 'massachusetts',
    name: 'Massachusetts',
    listType: 'fixed',
    hasCatchAll: true,
    ageRange: NO_MINORS_AGE_RANGE, // DMC-10795 Template B: remove Under-18 row
    catchAllNote:
      'Other debilitating conditions, as certified in writing by your provider.',
    notes: 'PTSD and epilepsy are not named in the statute.',
    conditions: [
      { id: 'aids-hiv', label: 'AIDS/HIV' },
      { id: 'als' },
      { id: 'cachexia', label: 'Cachexia / Wasting Syndrome' },
      { id: 'cancer' },
      { id: 'crohns', label: "Crohn's disease" },
      { id: 'chronic-pain', label: 'Chronic Pain (muscle & other)' },
      { id: 'glaucoma' },
      { id: 'hepatitis', label: 'Hepatitis (A, B, C)' },
      { id: 'ms', label: 'Multiple sclerosis (MS)' },
      { id: 'nausea', label: 'Nausea & Vomiting' },
      { id: 'parkinsons', label: "Parkinson's disease" },
      { id: 'peripheral-neuropathy', label: 'Peripheral Neuropathy' },
      { id: 'viral-cns', label: 'Viral/Prion diseases or CNS infections' },
    ],
  },
  md: {
    slug: 'maryland',
    name: 'Maryland',
    listType: 'fixed',
    hasCatchAll: true,
    catchAllNote:
      'Any severe chronic condition where other treatments were ineffective.',
    conditions: [
      { id: 'anorexia', label: 'Anorexia' },
      // Statute lists "Cachexia" and "Wasting Syndrome" separately; merged here
      // because they are clinical synonyms and two checkboxes would confuse.
      { id: 'cachexia', label: 'Cachexia / Wasting Syndrome' },
      { id: 'cancer' },
      { id: 'chronic-pain', label: 'Chronic Pain' },
      { id: 'epilepsy' },
      { id: 'glaucoma' },
      { id: 'aids-hiv' },
      { id: 'intractable-pain', label: 'Intractable or Severe Pain' },
      { id: 'ms', label: 'Multiple sclerosis (MS)' },
      { id: 'muscle-spasms', label: 'Persistent Muscle Spasms' },
      { id: 'ptsd' },
      { id: 'nausea', label: 'Severe Nausea' },
    ],
  },
  ct: {
    slug: 'connecticut',
    name: 'Connecticut',
    listType: 'fixed',
    hasCatchAll: false,
    ageRange: NO_MINORS_AGE_RANGE, // DMC-10795 Template C: remove Under-18 row
    notes: 'Large, heavily pain-specific list.',
    conditions: [
      { id: 'als' },
      { id: 'cachexia', label: 'Cachexia / Wasting Syndrome' },
      { id: 'cancer' },
      { id: 'cerebral-palsy', label: 'Cerebral Palsy' },
      {
        id: 'degenerative-spinal-pain',
        label: 'Chronic Neuropathic Pain with Degenerative Spinal Disorders',
      },
      {
        id: 'chronic-pain',
        label: 'Chronic Pain',
        note: 'At least 6 months, with an underlying condition, refractory to other treatment.',
      },
      { id: 'chronic-pancreatitis', label: 'Chronic Pancreatitis' },
      { id: 'crps', label: 'Complex Regional Pain Syndrome (I & II)' },
      { id: 'crohns', label: "Crohn's disease" },
      { id: 'cystic-fibrosis', label: 'Cystic Fibrosis' },
      {
        id: 'spinal-cord-spasticity',
        label: 'Spinal Cord Nerve Damage with Intractable Spasticity',
      },
      {
        id: 'ehlers-danlos',
        label: 'Ehlers-Danlos Syndrome with Chronic Pain',
      },
      { id: 'epilepsy' },
      { id: 'glaucoma' },
      { id: 'aids-hiv' },
      { id: 'hydrocephalus', label: 'Hydrocephalus with Intractable Headache' },
      { id: 'interstitial-cystitis', label: 'Interstitial Cystitis' },
      { id: 'intractable-headache', label: 'Intractable Headache Syndromes' },
      { id: 'intractable-pain', label: 'Intractable Neuropathic Pain' },
      {
        id: 'spinal-cord-injury',
        label: 'Irreversible Spinal Cord Injury with Intractable Spasticity',
      },
      { id: 'mals', label: 'MALS Syndrome' },
      {
        id: 'huntingtons',
        label: "Movement disorders with Huntington's disease",
      },
      { id: 'ms', label: 'Multiple sclerosis (MS)' },
      { id: 'muscular-dystrophy', label: 'Muscular Dystrophy' },
      { id: 'neuropathic-facial-pain', label: 'Neuropathic Facial Pain' },
      { id: 'osteogenesis-imperfecta', label: 'Osteogenesis Imperfecta' },
      { id: 'parkinsons', label: "Parkinson's disease" },
      { id: 'post-herpetic-neuralgia', label: 'Post-Herpetic Neuralgia' },
      {
        id: 'post-laminectomy',
        label: 'Post-Laminectomy Syndrome with Chronic Radiculopathy',
      },
      { id: 'ptsd' },
      { id: 'psoriasis', label: 'Severe Psoriasis & Psoriatic Arthritis' },
      { id: 'rheumatoid-arthritis', label: 'Severe Rheumatoid Arthritis' },
      { id: 'sickle-cell', label: 'Sickle Cell Disease' },
      {
        id: 'fibromyalgia',
        label: 'Spasticity or Neuropathic Pain with Fibromyalgia',
      },
      { id: 'terminal-illness', label: 'Terminal Illness (end-of-life)' },
      { id: 'tourette', label: 'Tourette Syndrome' },
      { id: 'ulcerative-colitis', label: 'Ulcerative Colitis' },
      { id: 'seizures', label: 'Uncontrolled Intractable Seizure Disorder' },
      { id: 'vulvodynia', label: 'Vulvodynia & Vulvar Burning' },
    ],
  },
  nj: {
    slug: 'new-jersey',
    name: 'New Jersey',
    listType: 'fixed',
    hasCatchAll: false,
    conditions: [
      { id: 'als' },
      { id: 'anxiety', label: 'Anxiety' },
      { id: 'cancer' },
      { id: 'chronic-pain', label: 'Chronic Pain' },
      { id: 'crohns', label: "Crohn's disease" },
      { id: 'dysmenorrhea' },
      { id: 'glaucoma' },
      { id: 'aids-hiv' },
      {
        id: 'ibd',
        label: 'Inflammatory bowel disease',
        note: 'e.g. Ulcerative Colitis.',
      },
      { id: 'spasticity', label: 'Intractable Skeletal Muscular Spasticity' },
      { id: 'headache-migraine', label: 'Migraine' },
      { id: 'ms', label: 'Multiple sclerosis (MS)' },
      { id: 'muscular-dystrophy', label: 'Muscular dystrophy' },
      { id: 'opioid-use-disorder', label: 'Opioid Use Disorder' },
      { id: 'ptsd' },
      { id: 'seizures', label: 'Seizure Disorders (incl. Epilepsy)' },
      { id: 'sickle-cell', label: 'Sickle cell anemia' },
      { id: 'terminal-illness', label: 'Terminal Illness (under 12 months)' },
      { id: 'tourette', label: 'Tourette Syndrome' },
    ],
  },
  pa: {
    slug: 'pennsylvania',
    name: 'Pennsylvania',
    listType: 'fixed',
    hasCatchAll: false,
    conditions: [
      { id: 'aids-hiv', label: 'AIDS/HIV' },
      { id: 'als' },
      { id: 'anxiety', label: 'Anxiety Disorders / Panic Disorders' },
      { id: 'autism', label: "Autism / Asperger's" },
      { id: 'cancer' },
      { id: 'chronic-pain', label: 'Chronic Pain (muscle & other)' },
      { id: 'cns-nerve-damage', label: 'CNS Nerve Damage' },
      {
        id: 'movement-disorders',
        label: 'Dyskinetic & Spastic Movement Disorders',
      },
      { id: 'epilepsy' },
      { id: 'glaucoma' },
      { id: 'hepatitis', label: 'Hepatitis (A, B, C)' },
      { id: 'huntingtons', label: "Huntington's disease" },
      { id: 'ibd', label: 'Inflammatory Bowel Disease (IBD)' },
      { id: 'ms', label: 'Multiple sclerosis (MS)' },
      { id: 'muscle-spasms', label: 'Muscle Spasms' },
      { id: 'neuropathy' },
      { id: 'opioid-use-disorder', label: 'Opioid Use Disorder' },
      { id: 'hospice-palliative', label: 'Palliative / Hospice Care' },
      { id: 'parkinsons', label: "Parkinson's disease" },
      { id: 'ptsd' },
      { id: 'seizures', label: 'Seizures & Seizure Disorders' },
      { id: 'sickle-cell', label: 'Sickle Cell Diseases (incl. Anemia)' },
      { id: 'spinal-cord-injury', label: 'Spinal Cord Injuries & Diseases' },
      { id: 'tourette', label: 'Tourette Syndrome' },
    ],
  },
  mo: {
    slug: 'missouri',
    name: 'Missouri',
    listType: 'fixed',
    hasCatchAll: true,
    catchAllNote:
      'Any other chronic debilitating condition, in your physician’s judgment.',
    conditions: [
      { id: 'aids-hiv', label: 'AIDS/HIV' },
      { id: 'alzheimers', label: "Alzheimer's" },
      { id: 'autism', label: 'Autism Spectrum Disorder' },
      { id: 'cachexia', label: 'Cachexia / Wasting Syndrome' },
      { id: 'cancer' },
      { id: 'crohns', label: "Crohn's disease" },
      {
        id: 'psychiatric-disorders',
        label: 'Debilitating Psychiatric Disorders',
        note: 'Includes anxiety, but must be diagnosed by a licensed psychiatrist.',
      },
      { id: 'epilepsy' },
      { id: 'glaucoma' },
      { id: 'hepatitis', label: 'Hepatitis C' },
      { id: 'huntingtons', label: "Huntington's disease" },
      { id: 'ibd', label: 'Inflammatory Bowel Diseases' },
      { id: 'headache-migraine', label: 'Intractable Migraines' },
      { id: 'ms', label: 'Multiple sclerosis (MS)' },
      { id: 'neuropathy', label: 'Neuropathies' },
      { id: 'parkinsons', label: "Parkinson's disease" },
      { id: 'ptsd' },
      { id: 'seizures', label: 'Seizures' },
      { id: 'sickle-cell', label: 'Sickle Cell Anemia' },
      { id: 'tourette', label: 'Tourette Syndrome' },
      {
        id: 'chronic-pain',
        label: 'Severe persistent pain or muscle spasms',
      },
      {
        id: 'prescription-alternative',
        label: 'Alternative to addictive prescription medication',
      },
      { id: 'terminal-illness', label: 'Terminal illness' },
    ],
  },
  ar: {
    slug: 'arkansas',
    name: 'Arkansas',
    listType: 'fixed',
    hasCatchAll: true,
    catchAllNote:
      'Any other condition approved by the Arkansas Department of Health.',
    inPersonRequired: true,
    notes:
      'New patients must be seen in person. MS qualifies only via the muscle-spasms clause.',
    conditions: [
      { id: 'alzheimers', label: "Alzheimer's" },
      { id: 'als' },
      { id: 'arthritis', label: 'Severe Arthritis' },
      { id: 'cachexia', label: 'Cachexia / Wasting Syndrome' },
      { id: 'cancer' },
      { id: 'crohns', label: "Crohn's disease" },
      { id: 'fibromyalgia' },
      { id: 'glaucoma' },
      { id: 'hepatitis', label: 'Hepatitis C' },
      { id: 'aids-hiv' },
      {
        id: 'intractable-pain',
        label: 'Intractable Pain',
        note: 'Pain lasting longer than 6 months.',
      },
      { id: 'nausea', label: 'Severe Nausea' },
      { id: 'peripheral-neuropathy', label: 'Peripheral Neuropathy' },
      { id: 'ptsd' },
      { id: 'seizures', label: 'Seizures (incl. epilepsy)' },
      {
        id: 'muscle-spasms',
        label: 'Severe or persistent muscle spasms',
        note: 'This is the route through which multiple sclerosis (MS) qualifies.',
      },
      { id: 'tourette', label: 'Tourette Syndrome' },
      { id: 'ulcerative-colitis', label: 'Ulcerative Colitis' },
    ],
  },
  la: {
    slug: 'louisiana',
    name: 'Louisiana',
    listType: 'fixed',
    hasCatchAll: true,
    catchAllNote: 'Any condition your provider considers debilitating.',
    conditions: [
      { id: 'alzheimers', label: "Alzheimer's" },
      { id: 'als' },
      { id: 'autism', label: 'Autism Spectrum Disorder' },
      { id: 'cachexia', label: 'Cachexia / Wasting Syndrome' },
      { id: 'cancer' },
      { id: 'concussion' },
      { id: 'crohns', label: "Crohn's disease" },
      { id: 'chronic-pain', label: 'Chronic Pain' },
      { id: 'epilepsy' },
      { id: 'glaucoma' },
      { id: 'aids-hiv' },
      { id: 'huntingtons', label: "Huntington's disease" },
      { id: 'intractable-pain', label: 'Intractable Pain' },
      { id: 'lewy-body', label: 'Lewy Body Dementia' },
      { id: 'motor-neuron-disease', label: 'Motor Neuron Disease' },
      { id: 'muscular-dystrophy', label: 'Muscular Dystrophy' },
      { id: 'ms', label: 'Multiple sclerosis (MS)' },
      { id: 'parkinsons', label: "Parkinson's disease" },
      { id: 'ptsd' },
      { id: 'seizures', label: 'Seizure Disorders' },
      { id: 'muscle-spasms', label: 'Severe Muscle Spasms' },
      { id: 'spasticity', label: 'Spasticity' },
      { id: 'spinal-muscular-atrophy', label: 'Spinal Muscular Atrophy' },
      { id: 'tbi', label: 'Traumatic Brain Injury' },
      { id: 'hospice-palliative', label: 'Hospice / palliative care' },
    ],
  },
  de: {
    slug: 'delaware',
    name: 'Delaware',
    listType: 'fixed',
    hasCatchAll: false,
    ageRange: NO_MINORS_AGE_RANGE, // DMC-10795 Template B: remove Under-18 row
    notes:
      'Anxiety qualifies for a CBD-rich card ONLY — this materially changes the offer.',
    conditions: [
      { id: 'aids-hiv', label: 'AIDS/HIV' },
      { id: 'alzheimers', label: "Alzheimer's" },
      { id: 'als' },
      {
        id: 'anxiety',
        label: 'Anxiety',
        note: 'Qualifies for a CBD-rich card only, not a full medical card.',
      },
      { id: 'autism', label: 'Autism with aggressive behavior' },
      { id: 'cancer' },
      { id: 'headache-migraine', label: 'Chronic debilitating migraine' },
      { id: 'decompensated-cirrhosis', label: 'Decompensated cirrhosis' },
      { id: 'glaucoma' },
      { id: 'ptsd' },
      { id: 'terminal-illness', label: 'Terminal illness' },
      { id: 'cachexia', label: 'Cachexia / Wasting Syndrome' },
      {
        id: 'chronic-pain',
        label: 'Severe debilitating pain',
        note: 'Lasting 3 months or more.',
      },
      { id: 'nausea', label: 'Intractable Nausea' },
      { id: 'seizures', label: 'Seizures' },
      {
        id: 'muscle-spasms',
        label: 'Severe or persistent muscle spasms',
        note: 'Includes multiple sclerosis (MS).',
      },
    ],
  },
  wv: {
    slug: 'west-virginia',
    name: 'West Virginia',
    listType: 'fixed',
    hasCatchAll: false,
    conditions: [
      { id: 'als' },
      { id: 'cancer' },
      { id: 'crohns', label: "Crohn's disease" },
      {
        id: 'spinal-cord-spasticity',
        label: 'Spinal Cord Nerve Damage with Intractable Spasticity',
      },
      { id: 'epilepsy' },
      { id: 'aids-hiv' },
      { id: 'huntingtons', label: "Huntington's disease" },
      { id: 'seizures', label: 'Intractable seizures' },
      { id: 'ms', label: 'Multiple sclerosis (MS)' },
      { id: 'neuropathy', label: 'Neuropathies' },
      { id: 'parkinsons', label: "Parkinson's disease" },
      { id: 'ptsd' },
      {
        id: 'chronic-pain',
        label: 'Severe chronic or intractable pain',
        note: 'Includes neuropathic pain.',
      },
      { id: 'sickle-cell', label: 'Sickle cell anemia' },
      {
        id: 'terminal-illness',
        label: 'Terminal illness',
        note: 'Life expectancy of approximately one year.',
      },
    ],
  },
  mt: {
    slug: 'montana',
    name: 'Montana',
    listType: 'fixed',
    hasCatchAll: false,
    ageRange: NO_MINORS_AGE_RANGE, // DMC-10795 Template C: remove Under-18 row
    conditions: [
      { id: 'cachexia', label: 'Cachexia / Wasting Syndrome' },
      { id: 'cancer' },
      { id: 'crohns', label: "Crohn's disease" },
      { id: 'epilepsy' },
      { id: 'glaucoma' },
      { id: 'nausea', label: 'Intractable Nausea / Vomiting' },
      { id: 'seizures', label: 'Intractable Seizure Disorder' },
      { id: 'ms', label: 'Multiple sclerosis (MS)' },
      { id: 'peripheral-neuropathy', label: 'Painful Peripheral Neuropathy' },
      { id: 'ptsd' },
      { id: 'chronic-pain', label: 'Severe Chronic Pain' },
      {
        id: 'spasticity',
        label: 'CNS disorder with chronic painful spasticity or muscle spasms',
      },
      { id: 'hospice-palliative', label: 'Hospice care' },
    ],
  },
  nm: {
    slug: 'new-mexico',
    name: 'New Mexico',
    listType: 'fixed',
    hasCatchAll: false,
    conditions: [
      { id: 'alzheimers', label: "Alzheimer's" },
      { id: 'als' },
      { id: 'anxiety', label: 'Anxiety Disorder' },
      { id: 'autism', label: 'Autism Spectrum Disorder' },
      { id: 'cancer' },
      { id: 'crohns', label: "Crohn's disease" },
      {
        id: 'spinal-cord-spasticity',
        label: 'Spinal Cord Nerve Damage with Intractable Spasticity',
      },
      { id: 'epilepsy', label: 'Epilepsy / Seizure Disorder' },
      { id: 'friedreichs-ataxia', label: "Friedreich's Ataxia" },
      { id: 'glaucoma' },
      {
        id: 'hepatitis',
        label: 'Hepatitis C',
        note: 'While on antiviral therapy.',
      },
      { id: 'aids-hiv' },
      { id: 'hospice-palliative', label: 'Hospice Care' },
      { id: 'huntingtons', label: "Huntington's disease" },
      { id: 'inclusion-body-myositis', label: 'Inclusion Body Myositis' },
      {
        id: 'inflammatory-arthritis',
        label: 'Inflammatory Autoimmune Arthritis',
      },
      { id: 'insomnia' },
      { id: 'nausea', label: 'Intractable Nausea / Vomiting' },
      { id: 'lewy-body', label: 'Lewy Body Disease' },
      { id: 'ms', label: 'Multiple sclerosis (MS)' },
      { id: 'sleep-apnea', label: 'Obstructive Sleep Apnea' },
      { id: 'opioid-use-disorder', label: 'Opioid Use Disorder' },
      { id: 'peripheral-neuropathy', label: 'Painful Peripheral Neuropathy' },
      { id: 'parkinsons', label: "Parkinson's disease" },
      { id: 'ptsd' },
      { id: 'anorexia', label: 'Severe Anorexia / Cachexia' },
      { id: 'chronic-pain', label: 'Severe Chronic Pain' },
      {
        id: 'spasmodic-torticollis',
        label: 'Spasmodic Torticollis (Cervical Dystonia)',
      },
      { id: 'spinal-muscular-atrophy', label: 'Spinal Muscular Atrophy' },
      { id: 'ulcerative-colitis', label: 'Ulcerative Colitis' },
    ],
  },
  mn: {
    slug: 'minnesota',
    name: 'Minnesota',
    listType: 'fixed',
    hasCatchAll: false,
    conditions: [
      { id: 'alzheimers', label: "Alzheimer's" },
      { id: 'als' },
      { id: 'autism', label: 'Autism' },
      {
        id: 'cancer',
        label: 'Cancer',
        note: 'With severe pain, nausea/vomiting, or cachexia.',
      },
      { id: 'chronic-pain', label: 'Chronic pain' },
      { id: 'tic-disorder', label: 'Chronic vocal or motor tic disorder' },
      { id: 'glaucoma' },
      { id: 'aids-hiv' },
      {
        id: 'ibd',
        label: 'Inflammatory bowel disease',
        note: "Includes Crohn's disease.",
      },
      { id: 'intractable-pain', label: 'Intractable Pain' },
      { id: 'ibs', label: 'Irritable bowel syndrome' },
      { id: 'ocd', label: 'Obsessive-compulsive disorder' },
      { id: 'sleep-apnea', label: 'Obstructive sleep apnea' },
      { id: 'ptsd' },
      { id: 'seizures', label: 'Seizures (incl. epilepsy)' },
      {
        id: 'muscle-spasms',
        label: 'Severe or persistent muscle spasms',
        note: 'Includes multiple sclerosis (MS).',
      },
      { id: 'sickle-cell', label: 'Sickle cell diseases' },
      {
        id: 'terminal-illness',
        label: 'Terminal illness',
        note: 'Life expectancy under one year.',
      },
      { id: 'tourette', label: 'Tourette syndrome' },
    ],
  },
  az: {
    slug: 'arizona',
    name: 'Arizona',
    listType: 'fixed',
    hasCatchAll: true,
    catchAllNote:
      'A chronic or debilitating disease causing severe pain, nausea, seizures, or muscle spasms.',
    conditions: [
      { id: 'aids-hiv', label: 'AIDS/HIV' },
      { id: 'alzheimers', label: "Alzheimer's" },
      { id: 'als' },
      { id: 'cachexia', label: 'Cachexia / Wasting Syndrome' },
      { id: 'cancer' },
      { id: 'chronic-pain', label: 'Chronic Pain' },
      { id: 'crohns', label: "Crohn's disease" },
      { id: 'epilepsy' },
      { id: 'glaucoma' },
      { id: 'hepatitis', label: 'Hepatitis C' },
      { id: 'ms', label: 'Multiple sclerosis (MS)' },
      { id: 'muscle-spasms', label: 'Muscle Spasms' },
      { id: 'ptsd' },
      { id: 'nausea', label: 'Severe Nausea' },
    ],
  },
  nv: {
    slug: 'nevada',
    name: 'Nevada',
    listType: 'fixed',
    hasCatchAll: false,
    notes:
      'PTSD is flagged ⚠️ in DMC-10802 as excluded — needs confirmation before shipping.',
    conditions: [
      { id: 'aids-hiv', label: 'AIDS/HIV' },
      { id: 'anxiety', label: 'Anxiety disorders' },
      { id: 'autism', label: 'Autism spectrum disorder' },
      { id: 'autoimmune-disease', label: 'Autoimmune disease' },
      { id: 'anorexia', label: 'Anorexia nervosa' },
      { id: 'cachexia', label: 'Cachexia' },
      { id: 'cancer' },
      { id: 'opioid-use-disorder', label: 'Opioid dependence or addiction' },
      { id: 'glaucoma' },
      {
        id: 'muscle-spasms',
        label: 'Muscle spasms',
        note: 'Includes multiple sclerosis (MS).',
      },
      { id: 'nausea', label: 'Nausea / vomiting' },
      { id: 'peripheral-neuropathy', label: 'Painful peripheral neuropathy' },
      { id: 'neuropathic-condition', label: 'Neuropathic condition' },
      { id: 'seizures', label: 'Seizures (incl. epilepsy)' },
      { id: 'chronic-pain', label: 'Severe or chronic pain' },
    ],
  },
  ca: {
    slug: 'california',
    name: 'California',
    listType: 'fixed',
    hasCatchAll: true,
    catchAllNote:
      'Any condition or symptom that limits major life activities, at your physician’s discretion.',
    notes:
      "Group 2 — the 2026-07-17 DMC-10802 update enumerates Group 1 and CA is not in it, so the list IS rendered despite the physician-discretion catch-all. Option A implied PTSD/ALS/Crohn's also qualify here; Option B omits them. Option B confirmed correct by Aurélien 2026-07-17.",
    conditions: [
      { id: 'aids-hiv', label: 'AIDS' },
      { id: 'anorexia' },
      { id: 'arthritis' },
      { id: 'cachexia', label: 'Cachexia (Wasting Syndrome)' },
      { id: 'cancer' },
      { id: 'chronic-pain', label: 'Chronic pain' },
      { id: 'glaucoma' },
      { id: 'headache-migraine', label: 'Migraine' },
      {
        id: 'muscle-spasms',
        label: 'Persistent muscle spasms',
        note: 'Includes multiple sclerosis (MS).',
      },
      { id: 'seizures', label: 'Seizures (incl. epilepsy)' },
      { id: 'nausea', label: 'Severe nausea' },
    ],
  },
  hi: {
    slug: 'hawaii',
    name: 'Hawaii',
    listType: 'fixed',
    hasCatchAll: true,
    catchAllNote:
      'A chronic or debilitating disease producing cachexia, severe pain, severe nausea, seizures, or severe muscle spasms.',
    notes:
      "Chronic pain and Crohn's qualify ONLY via the symptom catch-all, not as named conditions.",
    conditions: [
      { id: 'aids-hiv', label: 'AIDS/HIV' },
      { id: 'als' },
      { id: 'cancer' },
      { id: 'epilepsy' },
      { id: 'glaucoma' },
      { id: 'lupus' },
      { id: 'ms', label: 'Multiple sclerosis (MS)' },
      { id: 'ptsd' },
      { id: 'rheumatoid-arthritis', label: 'Rheumatoid Arthritis' },
    ],
  },
  vt: {
    slug: 'vermont',
    name: 'Vermont',
    listType: 'fixed',
    hasCatchAll: true,
    ageRange: NO_MINORS_AGE_RANGE, // DMC-10795 Template C: remove Under-18 row
    catchAllNote:
      'A condition producing chronic pain, seizures, severe nausea, or wasting syndrome.',
    conditions: [
      { id: 'aids-hiv', label: 'AIDS/HIV' },
      { id: 'cancer' },
      { id: 'crohns', label: "Crohn's disease" },
      { id: 'glaucoma' },
      { id: 'ms', label: 'Multiple sclerosis (MS)' },
      { id: 'parkinsons', label: "Parkinson's disease" },
      { id: 'ptsd' },
      { id: 'ulcerative-colitis', label: 'Ulcerative colitis' },
    ],
  },
};

/** Every state Leafwell operates in. Drives the Task 1 state selector. */
export const SUPPORTED_STATE_CODES = Object.keys(STATE_QUALIFY_CONFIG);

/** Look up by two-letter code (`fl`) — case-insensitive. */
export const getStateConfig = (
  stateCode: string | null | undefined,
): StateQualifyConfig | null =>
  stateCode ? STATE_QUALIFY_CONFIG[stateCode.toLowerCase()] ?? null : null;

/** Is this a state where Leafwell operates? Drives the waitlist fallback. */
export const isSupportedState = (
  stateCode: string | null | undefined,
): boolean => Boolean(getStateConfig(stateCode));

/** Resolve the label a user should actually see for an entry. */
export const getConditionLabel = (entry: StateConditionEntry): string =>
  entry.label ?? CONDITION_LABELS[entry.id];

/**
 * Order a state's conditions: pinned-by-popularity first, then alphabetical.
 * Per Aurélien on DMC-10802.
 */
export const sortStateConditions = (
  entries: StateConditionEntry[],
): StateConditionEntry[] => {
  const pinned: StateConditionEntry[] = [];
  const rest: StateConditionEntry[] = [];

  entries.forEach(entry => {
    (PINNED_CONDITIONS.includes(entry.id) ? pinned : rest).push(entry);
  });

  pinned.sort(
    (a, b) => PINNED_CONDITIONS.indexOf(a.id) - PINNED_CONDITIONS.indexOf(b.id),
  );
  rest.sort((a, b) => getConditionLabel(a).localeCompare(getConditionLabel(b)));

  return [...pinned, ...rest];
};

/** The conditions to render for a state, in display order. */
export const getStateConditions = (
  stateCode: string,
): StateConditionEntry[] => {
  const config = getStateConfig(stateCode);
  if (!config) return [];
  return sortStateConditions(config.conditions);
};

/** Should the checklist step be shown at all? `none` states skip straight to results. */
export const shouldShowConditionsStep = (stateCode: string): boolean => {
  const config = getStateConfig(stateCode);
  return Boolean(config && config.listType !== 'none');
};

/** Age buckets to offer for a state (DEFAULT_AGE_RANGE unless overridden). */
export const getStateAgeRange = (stateCode: string): string[] =>
  getStateConfig(stateCode)?.ageRange ?? DEFAULT_AGE_RANGE;
